import {
  arrayIsNullOrEmpty,
  AutoKey,
  BaseClipBoardButton,
  BaseToolbar,
  BaseToolbarButton
} from '@invara-dev/react-base';
import { EmailAddressModel } from '../../globalModels/common/emailAddressModel.ts';
import { PhoneNumberModel } from '../../globalModels/common/phoneNumberModel.ts';
import { Anchorme } from 'react-anchorme';
import { formatPhoneNumber } from '../../util/helpers/crmHelpers.ts';
import {
  faBriefcase,
  faEnvelope,
  faFax,
  faGlobe,
  faMobile,
  faPerson,
  faPhone,
  faStar
} from '@fortawesome/free-solid-svg-icons';
import { Box } from '@chakra-ui/react';
import addTextMagicDomain from '../../util/formatters/phoneToEmailFormatter.ts';
import { PhoneNumberType } from '../../enums/crm/PhoneNumberType.ts';
import { EmailAddressType } from '../../enums/crm/EmailAddressType.ts';

type Props = {
  emails: Partial<EmailAddressModel>[];
  phoneNumbers: Partial<PhoneNumberModel>[];
  faxNumbers: string[];
  website?: {
    url: string;
    description: string;
  };
};
/**
 * ContactInfoBar component displays a toolbar of contact methods including emails, phone numbers, fax numbers, and an optional website.
 *
 * Responsibilities:
 * - Checks if any contact props are provided; returns null if all are empty.
 * - Renders a <BaseToolbar> containing:
 *   - Email buttons via <AutoKey> and <BaseClipBoardButton>, opening mailto links or copying addresses.
 *   - Phone buttons via <AutoKey> and <BaseClipBoardButton>, formatting numbers and providing call/text options.
 *   - Fax buttons via <AutoKey>, formatting fax numbers into email-to-fax links.
 *   - A website button if a URL is provided, opening the site in a new tab.
 * - Displays an instructional note below the toolbar.
 *
 * @component
 * @param {{
 *   emails: Partial<EmailAddressModel>[];       // Array of email objects with Address, Description, and optional Type
 *   phoneNumbers: Partial<PhoneNumberModel>[];  // Array of phone number objects with Number, Description, and optional Type
 *   faxNumbers: string[];                      // Array of fax number strings
 *   website?: {                                // Optional website link object
 *     url: string;
 *     description: string;
 *   };
 * }} props
 * @returns {JSX.Element|null} A toolbar of contact buttons or null if no contact data.
 */
export const ContactInfoBar = (props: Props) => {
  if (
    arrayIsNullOrEmpty(props.emails) &&
    arrayIsNullOrEmpty(props.phoneNumbers) &&
    arrayIsNullOrEmpty(props.faxNumbers) &&
    !props.website
  ) {
    return null;
  }

  return (
    <>
      <BaseToolbar>
        <AutoKey items={props.emails.filter((val) => val.Address)}>
          {(email) => {
            const getEmailIcon = (type: EmailAddressType | undefined) => {
              switch (type) {
                case EmailAddressType.Primary:
                  return faStar;
                case EmailAddressType.Business:
                  return faBriefcase;
                case EmailAddressType.Personal:
                  return faPerson;
                default:
                  return faEnvelope;
              }
            };

            const getEmailLabel = (type: EmailAddressType | undefined) => {
              switch (type) {
                case EmailAddressType.Primary:
                  return 'Primary';
                case EmailAddressType.Business:
                  return 'Business';
                case EmailAddressType.Personal:
                  return 'Personal';
                default:
                  return 'Email';
              }
            };

            const tooltipContent = `${getEmailLabel(email.Type)}: ${email.Address} (Click for options)`;

            return (
              <>
                {email.Address && (
                  <BaseClipBoardButton
                    icon={getEmailIcon(email.Type)}
                    toolTip={tooltipContent}
                    links={[
                      {
                        text: 'Email',
                        apply: `mailto:`,
                        openWindow: true
                      }
                    ]}>
                    {email.Address}
                  </BaseClipBoardButton>
                )}
              </>
            );
          }}
        </AutoKey>
        <AutoKey items={props.phoneNumbers.filter((val) => val.Number)}>
          {(phoneNumber) => {
            return (
              <>
                {phoneNumber.Number && (
                  <BaseClipBoardButton
                    display={formatPhoneNumber(phoneNumber.Number) ?? phoneNumber.Number ?? ''}
                    icon={phoneNumber.Type == PhoneNumberType.Mobile ? faMobile : faPhone}
                    toolTip={
                      phoneNumber.Description ??
                      `${PhoneNumberType[phoneNumber.Type ?? PhoneNumberType.Business]} Phone Number`
                    }
                    links={[
                      {
                        text: 'Call',
                        apply: `tel:`,
                        openWindow: true
                      },
                      { text: 'Copy Text Magic', apply: addTextMagicDomain },
                      { text: 'Text', apply: (x) => `tel:${addTextMagicDomain(x)}`, openWindow: true }
                    ]}>
                    {formatPhoneNumber(phoneNumber.Number, true) ?? ''}
                  </BaseClipBoardButton>
                )}
              </>
            );
          }}
        </AutoKey>
        <AutoKey items={props.faxNumbers}>
          {(faxNumber) => {
            return (
              <BaseClipBoardButton
                links={[{ text: 'Fax', apply: 'mailto:', openWindow: true }]}
                display={formatPhoneNumber(faxNumber) ?? faxNumber}
                toolTip={'Fax Number'}
                icon={faFax}>
                {`${faxNumber}@fax.telus.net`}
              </BaseClipBoardButton>
            );
          }}
        </AutoKey>
        {props.website && (
          <BaseToolbarButton toolTip={'Website'} icon={faGlobe}>
            <Anchorme target='_blank'>{props.website.url}</Anchorme>
          </BaseToolbarButton>
        )}
      </BaseToolbar>
      <Box w={'100%'} lineHeight={'10px'} m={'0'} p={'0 0 4px 0'} fontSize={'xs'} fontStyle={'italic'}>
        New: open new tabs to send messages directly!, click the buttons to open the menu
      </Box>
    </>
  );
};
